import { GraphQLClient } from 'graphql-request';
import { print } from 'graphql';
import { ShopifyComparisonQuery } from '@/components/shop/collections/Collection';
import ProductByHandleQuery from '@/graphql/shopify/product/ProductByHandleQuery.gql';
import ProductCardByHandleQuery from '@/graphql/shopify/product/ProductCardByHandleQuery.gql';
import ProductBySkuQuery from '@/graphql/shopify/product/ProductBySkuQuery.gql';
import ShopifyCollectionQuery from '@/graphql/shopify/collection/ShopifyCollectionQuery.gql';
import * as Sentry from '@sentry/vue';
import { useLoyalty } from '@/composables/useLoyalty';

export default defineNuxtPlugin(function (nuxtApp) {
  const { locale } = nuxtApp.$i18n;
  const { shopifyApiVersion } = nuxtApp.$config.public;

  // Call useLoyalty during plugin initialization (context exists here)
  const { isLoyaltyUser, loyaltyDiscount } = useLoyalty();

  let client = null;

  const createClient = (locale) => {
    const { shopifyApiAccessToken, shopifyApiDomain } = nuxtApp.$getCurrentLocaleConfig(locale) || {};

    if (!shopifyApiAccessToken) return Promise.reject(new Error(`shopifyApiAccessToken not set for locale: ${locale}`));
    if (!shopifyApiDomain) return Promise.reject(new Error(`shopifyApiDomain not set for locale: ${locale}`));

    const endPoint = `https://${shopifyApiDomain}/api/${shopifyApiVersion}/graphql`;

    client = new GraphQLClient(endPoint, {
      headers: {
        'X-Shopify-Storefront-Access-Token': shopifyApiAccessToken,
        'accept-language': locale,
        Accept: 'application/json',
      },
    });
  };

  createClient(locale.value);

  const transformLangVariable = function (variables) {
    if (!variables) {
      return {};
    }

    const map = {
      'ch-fr': 'fr',
      'ch-de': 'de',
    };

    if (variables.lang && map[variables.lang]) {
      variables.lang = map[variables.lang];
    }

    if (variables.lang) {
      variables.lang = variables.lang.toUpperCase();
    }

    return variables;
  };

  const request = (query, variables) => {
    const stringyfiedQuery = typeof query === 'object' && query.kind === 'Document' ? print(query) : query;

    return client.request(stringyfiedQuery, transformLangVariable(variables));
  };

  const collectionRequest = (rawVariables) => {
    const variables = { ...rawVariables };
    if (rawVariables.limit) variables.limit = parseInt(rawVariables.limit);

    return request(ShopifyCollectionQuery, variables)
      .then((data) => {
        let collection = data.collection;

        if (isLoyaltyUser.value) {
          // modify product price if product has loyalty discount tag
          collection.products.edges = collection.products.edges.map((product) => {
            product.node = loyaltyDiscount(product.node);
            return product;
          });
        }

        return collection;
      })
      .catch((error) => {
        Sentry.captureException(error);
        return null;
      });
  };

  const comparisonRequest = (rawVariables) => {
    const variables = { ...rawVariables, lang: locale.value };
    if (rawVariables.limit) variables.limit = parseInt(rawVariables.limit);

    return request(ShopifyComparisonQuery, variables);
  };

  const productsRequest = (handles) => {
    const singleProductRequest = (handle) =>
      request(ProductCardByHandleQuery, { handle, lang: locale.value })
        .then((data) => {
          if (!data.product) {
            console.warn(`Product Data for handle '${handle}' could not be found!`);
          }

          let product = data.product;

          if (isLoyaltyUser.value) {
            // modify product price if product has loyalty discount tag
            product = loyaltyDiscount(product);
          }

          return product;
        })
        .catch((error) => {
          console.warn(`Product Data for handle '${handle}' could not be found!`);
          Sentry.captureException(error);
          return null;
        });

    return Promise.all(handles.map(singleProductRequest));
  };

  const shopifyProductByHandle = (handle) => {
    return request(ProductByHandleQuery, { handle, lang: locale.value })
      .then((data) => {
        if (!data.productByHandle) {
          console.warn(`Product Data for handle '${handle}' could not be found!`);
          return null;
        }

        let product = data.productByHandle;

        // Apply loyalty discount if user is loyalty member
        if (isLoyaltyUser.value) {
          product = loyaltyDiscount(product);
        }

        return product;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('something went wrong fetching product by handle', handle, error);
        return null;
      });
  };

  const shopifyProductBySku = (sku) => {
    const normalizedSku = sku.toUpperCase();

    return request(ProductBySkuQuery, { query: normalizedSku, lang: locale.value })
      .then((data) => {
        const products = data.products?.edges || [];

        for (const productEdge of products) {
          let product = productEdge.node;
          const variant = product.variants?.edges?.find((v) => v.node.sku?.toUpperCase() === normalizedSku)?.node;

          if (variant) {
            if (isLoyaltyUser.value) {
              product = loyaltyDiscount(product);
            }

            return { product, variant };
          }
        }

        return null;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error fetching product by SKU', sku, error);
        return null;
      });
  };

  return {
    provide: {
      shopifyProductByHandle,
      shopifyProductBySku,
      shopifyProductsRequest: productsRequest,
      shopifyComparisonRequest: comparisonRequest,
      shopifyCollectionRequest: collectionRequest,
      shopifyRequest: request,
      shopifyRefreshClient: createClient,
    },
  };
});
